/**
 * LobbyToolsContent
 *
 * Player-facing "play with the lobby" panel, propless (Phase 2 fold). Shown
 * only when the player is in a lobby map — the QUICK MENU button that opens it
 * (`windowBridge.toggle('lobbyTools')`) is `lobbyOnly`. Sends a single Colyseus
 * `lobby_tool` message per button press; the server enforces rate limits,
 * concurrency caps, and the allow-list.
 *
 * WindowHost mounts this body inside the shared `ToolWindow` chrome (only while
 * the `lobbyTools` window is open), so it no longer owns its own ToolWindow /
 * `isOpen` / `onClose`. Player-free: reads the NetworkManager singleton only.
 *
 * This is a **scaffold**. The final button list, icons, and copy are
 * expected to be tuned before the live lobby goes 24/7.
 */

import React, { useState } from 'react';
import { Button } from '../primitives/Button';
import { COLORS, SPACE, RADII, FONT_SIZE, FONTS } from '../theme';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import {
  LOBBY_TOOL_COOLDOWN_MS,
  LOBBY_TOOL_DEFINITIONS,
  LOBBY_TOOL_MAX_BOTS_PER_PLAYER,
  LOBBY_TOOL_MAX_MOBS_PER_PLAYER,
  LOBBY_TOOL_MESSAGE,
  LobbyToolId,
} from '../../../shared/constants/LobbyTools';

const sendLobbyTool = (tool: LobbyToolId): boolean => {
  const nm = ServiceRegistry.getInstance().get('NetworkManager');
  const room = nm?.getRoom?.();
  if (!room) {
    gameLogger.warn('lobby-tools', 'No Colyseus room available for lobby_tool send');
    return false;
  }
  room.send(LOBBY_TOOL_MESSAGE, { tool });
  return true;
};

export const LobbyToolsContent: React.FC = () => {
  const [cooldownUntil, setCooldownUntil] = useState(0);

  const now = Date.now();
  const remainingMs = Math.max(0, cooldownUntil - now);
  const onCooldown = remainingMs > 0;

  const handleClick = (tool: LobbyToolId) => {
    if (onCooldown) return;
    if (!sendLobbyTool(tool)) return;
    // Mirror the server-side cooldown on the client so the UI feels honest.
    setCooldownUntil(Date.now() + LOBBY_TOOL_COOLDOWN_MS);
  };

  return (
    <div
      data-testid="lobby-tools-panel"
      style={{ padding: SPACE.lg, color: COLORS.textPrimary, fontFamily: FONTS.ui }}
    >
      {/* Subtitle — explains the hardening at a glance */}
      <div style={{
        fontSize: FONT_SIZE.sm,
        color: COLORS.textSecondary,
        marginBottom: SPACE.lg,
        lineHeight: 1.4,
      }}>
        Player-safe, rate-limited. Works only in the live lobby.
        Caps: {LOBBY_TOOL_MAX_MOBS_PER_PLAYER} mobs / {LOBBY_TOOL_MAX_BOTS_PER_PLAYER} bots per player.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: SPACE.sm }}>
        {LOBBY_TOOL_DEFINITIONS.map((t) => (
          <Button
            key={t.id}
            data-testid={`lobby-tool-${t.id}`}
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => handleClick(t.id)}
            disabled={onCooldown}
            title={t.description}
            iconLeft={<span style={{ fontSize: FONT_SIZE.xxl }}>{t.icon}</span>}
            style={{ justifyContent: 'flex-start' }}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {onCooldown && (
        <div style={{
          marginTop: SPACE.md,
          padding: `${SPACE.sm}px ${SPACE.md}px`,
          fontSize: FONT_SIZE.sm,
          color: COLORS.warning,
          background: COLORS.surfaceHover,
          borderRadius: RADII.md,
        }}>
          Cooldown: {(remainingMs / 1000).toFixed(1)}s
        </div>
      )}
    </div>
  );
};
